import * as React from 'react';
import { useMutation } from 'react-query';
import XLSX from 'sheetjs-style-v2';
import * as FileSaver from 'file-saver';
import type { RestrictionEvent } from 'contracts/restricted-products/restricted-products';
import type { Filters } from 'hooks/store/restricted-products/use-restriction-event-search-params';
import { useFetchRestrictionEventsReport } from 'hooks/api/restricted-products';
import { notify } from 'services';
import { Formatter } from 'utils';

type Props = {
  filters: Filters
}

export const RestrictionEventsReport = ({ filters }: Props) => {
  const { fetchRestrictionEventsReport } = useFetchRestrictionEventsReport();

  const downloadReport = React.useCallback((reportData: RestrictionEvent[]) => {
    // Evita gerar uma planilha vazia quando os filtros não retornam registros.
    if (!reportData.length) {
      notify('warning', 'Não há registros para exportar.');
      return;
    }

    // Converte os eventos para as mesmas colunas e formatos exibidos na tabela.
    const payload = reportData.map((item) => {
      const action = item.action === 'block' ? 'Bloqueado' : 'Desbloqueado';

      return {
        Id: item.id,
        Planta: item.plant,
        Material: item.material,
        'Desc. material': item.materialDescription,
        'Quant.': [item.quantity, item.materialUnit]
          .filter(value => value !== null && value !== undefined && value !== '')
          .join(' ')
          .toUpperCase(),
        Lote: item.batch,
        'Ação': `${action} em ${Formatter.dateFull(item.createdAt)}`,
        Status: item.justification ? 'justificado' : 'pendente de justificativa',
      };
    });

    // Monta o arquivo XLSX em memória antes de iniciar o download no navegador.
    const worksheet = XLSX.utils.json_to_sheet(payload);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    const date = new Date().toISOString().slice(0, 10);

    FileSaver.saveAs(file, `RELATORIO-EVENTOS-PRODUTOS-RESTRITOS-${date}.xlsx`);
  }, []);

  // Busca todos os eventos que correspondem aos filtros ativos e gera o relatório.
  const reportMutation = useMutation({
    mutationKey: ['fetch-restriction-events-report', filters],
    mutationFn: () => fetchRestrictionEventsReport({ filters }),
    onSuccess: downloadReport,
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  return (
    <button
      type={'button'}
      onClick={() => reportMutation.mutate()}
      disabled={reportMutation.isLoading}
      className={
        'flex h-[30px] items-center gap-2 rounded-full bg-[#0CC769] px-4 text-sm text-white disabled:cursor-not-allowed disabled:opacity-80'
      }
    >
      <span>Exportar Excel</span>
      {reportMutation.isLoading ? (
        <span
          className={
            'h-4 w-4 animate-spin rounded-full border-2 border-white border-t-[#0CC769]'
          }
        />
      ) : null}
    </button>
  );
};
